// frontend/src/components/ComplianceView.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Chip,
  Link,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import {
  ExpandMore,
  OpenInNew,
  Search,
  Code,
  Security,
  Description,
  Download
} from '@mui/icons-material';
import axios from '../utils/axios';
import versionService from '../services/versionService';

const ComplianceView = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [licenseFilter, setLicenseFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [appVersion, setAppVersion] = useState('0.1.0'); // Default app version

  // Fetch packages from API
  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/compliance/packages');
      setPackages(response.data.packages || []);
      setLastUpdated(response.data.last_updated);
    } catch (err) {
      console.error('Error fetching compliance data:', err);
      setError('Failed to load compliance data. Please try again later.');
      // Fallback to empty array if API fails
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
    fetchVersionInfo();
  }, []);

  // Fetch version info from centralized service
  const fetchVersionInfo = async () => {
    try {
      const versionString = await versionService.getVersion();
      // Use the full version string (includes environment tags like -dev, -stg)
      setAppVersion(versionString);
    } catch (error) {
      console.error('Failed to fetch version:', error);
      // Keep the default version
    }
  };

  // Static packages as fallback (removed most content for brevity)
  const fallbackPackages = [
    // Fallback packages in case API fails
    {
      name: 'React',
      version: '18.2.0',
      license: 'MIT',
      category: 'JavaScript Package',
      description: 'A JavaScript library for building user interfaces',
      homepage: 'https://reactjs.org/',
      repository: 'https://github.com/facebook/react',
      maintainer: 'Meta Platforms, Inc.'
    },
    {
      name: 'FastAPI',
      version: '0.109.0',
      license: 'MIT',
      category: 'Python Package',
      description: 'FastAPI framework, high performance, easy to learn, fast to code, ready for production',
      homepage: 'https://fastapi.tiangolo.com/',
      repository: 'https://github.com/tiangolo/fastapi',
      maintainer: 'Sebastián Ramírez'
    }
  ];

  // Use fallback packages if API data is not available
  const currentPackages = packages.length > 0 ? packages : fallbackPackages;

  // Filter packages based on search and filters
  const filteredPackages = currentPackages.filter(pkg => {
    const matchesSearch = !searchQuery || 
      pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesLicense = !licenseFilter || pkg.license === licenseFilter;
    const matchesCategory = !categoryFilter || pkg.category === categoryFilter;
    
    return matchesSearch && matchesLicense && matchesCategory;
  });

  // Get unique values for filters
  const uniqueLicenses = [...new Set(currentPackages.map(pkg => pkg.license))].sort();
  const uniqueCategories = [...new Set(currentPackages.map(pkg => pkg.category))].sort();

  // Calculate license distribution for summary cards
  const licenseStats = {};
  currentPackages.forEach(pkg => {
    const license = pkg.license || 'Unknown';
    licenseStats[license] = (licenseStats[license] || 0) + 1;
  });
  
  // Get top 3 licenses and group others
  const sortedLicenses = Object.entries(licenseStats)
    .sort(([,a], [,b]) => b - a);
  
  const topLicenses = sortedLicenses.slice(0, 3);
  const otherCount = sortedLicenses.slice(3).reduce((sum, [, count]) => sum + count, 0);

  const getLicenseColor = (license) => {
    switch (license) {
      case 'MIT': return 'success';
      case 'Apache-2.0': return 'info';
      case 'BSD-3-Clause': 
      case 'BSD-2-Clause': return 'warning';
      case 'ISC': return 'secondary';
      case 'GPL-2.0':
      case 'GPL-3.0': return 'error';
      default: return 'default';
    }
  };

  const getLicenseIcon = (license) => {
    switch (license) {
      case 'MIT': return Security;
      case 'Apache-2.0': return Description;
      case 'BSD-3-Clause':
      case 'BSD-2-Clause': return Security;
      default: return Code;
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setLicenseFilter('');
    setCategoryFilter('');
  };

  // Generate CSV download
  const downloadCSV = () => {
    const headers = ['Name', 'Version', 'License', 'Category', 'Description', 'Maintainer', 'Homepage', 'Repository'];
    const csvContent = [
      headers.join(','),
      ...filteredPackages.map(pkg => [
        `"${pkg.name || ''}"`,
        `"${pkg.version || ''}"`,
        `"${pkg.license || ''}"`,
        `"${pkg.category || ''}"`,
        `"${(pkg.description || '').replace(/"/g, '""')}"`,
        `"${pkg.maintainer || ''}"`,
        `"${pkg.homepage || ''}"`,
        `"${pkg.repository || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lps-app-packages-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5">
            Open Source Compliance
          </Typography>
        </Box>
        
        {/* Compliance Notice and Version Info - Moved from bottom */}
        <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.default' }}>
          <Typography variant="h6" gutterBottom>
            License Compliance Notice
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            LPS-App is built using various open source software packages. We are committed to respecting 
            the licenses of all third-party software we use. This page provides transparency about our 
            dependencies and their licensing terms.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                <strong>App Version:</strong> {appVersion}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Last Updated:</strong> {lastUpdated || new Date().toLocaleDateString()}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadCSV}
              disabled={filteredPackages.length === 0}
            >
              Download CSV
            </Button>
          </Box>
        </Paper>
        <Typography variant="body2" color="textSecondary">
          This page lists all open source software packages used in LPS-App, their licenses, and compliance information.
          {lastUpdated && (
            <Box component="span" sx={{ ml: 1 }}>
              Last updated: {lastUpdated}
            </Box>
          )}
        </Typography>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Show content when not loading */}
      {!loading && (
        <>
          {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Total Packages Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Code sx={{ mr: 2, color: 'primary.main', fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Packages
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {currentPackages.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Dynamic License Cards */}
        {topLicenses.map(([license, count], index) => {
          const IconComponent = getLicenseIcon(license);
          const color = getLicenseColor(license);
          return (
            <Grid item xs={12} sm={6} md={3} key={license}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconComponent sx={{ mr: 2, color: `${color}.main`, fontSize: 40 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        {license}
                      </Typography>
                      <Typography variant="h4" color={`${color}.main`}>
                        {count}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}

        {/* Other Licenses Card (if there are more than 3 license types) */}
        {otherCount > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Code sx={{ mr: 2, color: 'text.secondary', fontSize: 40 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Other Licenses
                    </Typography>
                    <Typography variant="h4" color="text.secondary">
                      {otherCount}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search packages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>License</InputLabel>
              <Select
                value={licenseFilter}
                label="License"
                onChange={(e) => setLicenseFilter(e.target.value)}
              >
                <MenuItem value="">All Licenses</MenuItem>
                {uniqueLicenses.map((license) => (
                  <MenuItem key={license} value={license}>
                    {license}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {uniqueCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <button
                onClick={clearFilters}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                Clear
              </button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Packages List */}
      <Typography variant="h6" gutterBottom>
        Open Source Packages ({filteredPackages.length})
      </Typography>
      
      {filteredPackages.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No packages found matching your search criteria.
        </Alert>
      )}

      {filteredPackages.map((pkg, index) => (
        <Accordion key={pkg.name} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="div">
                  {pkg.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  v{pkg.version} • {pkg.category} • {pkg.maintainer}
                </Typography>
              </Box>
              <Chip
                label={pkg.license}
                color={getLicenseColor(pkg.license)}
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="body1" gutterBottom>
                  <strong>Description:</strong> {pkg.description || 'No description available'}
                </Typography>
                {pkg.usage && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Usage in LPS-App:</strong> {pkg.usage}
                  </Typography>
                )}
                <Typography variant="body1" gutterBottom>
                  <strong>Maintainer:</strong> {pkg.maintainer || 'Unknown'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {pkg.homepage && (
                    <Link
                      href={pkg.homepage}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      Homepage <OpenInNew fontSize="small" />
                    </Link>
                  )}
                  {pkg.repository && (
                    <Link
                      href={pkg.repository}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      Repository <OpenInNew fontSize="small" />
                    </Link>
                  )}
                  {pkg.licenseUrl && (
                    <Link
                      href={pkg.licenseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      License Text <OpenInNew fontSize="small" />
                    </Link>
                  )}
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

        </>
      )}
    </Box>
  );
};

export default ComplianceView;